import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { Button, LinkButton, SecondaryButton } from "../ui/button";
import { Group, PLATE } from "../shell/surfaces";
import { API_URL } from "../../lib/config";
import {
  TIER_NAMES,
  startPlanRoad,
  type LadderRung,
  type PlanChangeOption,
  type PlanChangePreview,
  type PlanInterval,
  type PlanOffer,
  type PlanTier,
  type PlanView,
} from "../../lib/api";
import { PlanCard } from "./plan-card";
import { START_TRIAL } from "./trial-gate";
import { PlanNotices } from "./plan-notices";

export const PLAN_TITLE = "Plan";

/**
 * WHERE THE CHANGE CONTROLS WERE, WHILE A PAYMENT IS FAILING (ruling R-1006-3).
 * An upgrade, a downgrade and an interval switch are refused by the server on a
 * `past_due` plan, so the screen offers none of them: this sentence and the one
 * action that fixes it. Cancelling stays.
 */
export const UPDATE_CARD_FIRST = "Update your card first.";

/** The spent line while a payment is failing: nothing resets, so it does not say so (ruling R-1006-3). */
export const PAST_DUE_USED_LINE =
  "This period's allowance is used. Work resumes once your payment goes through.";

/**
 * A payment dispute is open, so paid features are off (ToU 2.2 §8.9). Stated
 * once, quietly, and without an amount — the figure is between the account
 * holder and their bank, and repeating our side of it here would read as an
 * argument rather than a notice.
 */
export const PAID_SUSPENDED_LINE =
  "Paid features are suspended while a payment dispute is open on this account.";
export const PLAN_LEDE =
  "A plan pays for the AI work you ask your doorman for. Your door, your knocks and " +
  "answering by hand are free at any volume, on every plan.";

/**
 * THE RENEWAL CONSENT IS ITS OWN CONTROL, AND IT IS NOT THE TERMS (ToU 2.1
 * §8.2, ruling R-0914-1 (2)).
 *
 * The old label read "I agree to these terms and to this recurring charge",
 * which bundled two different things into one tick: acceptance of a long
 * document, and express informed consent to money moving on a schedule. §8.2
 * now says the agreement to the recurring charge "is a control of its own: it
 * is not part of accepting these Terms, and it is not the control that submits
 * payment" — so the bundle is split, the checkbox consents to the CHARGE, and
 * the Terms sit beside it as a link, accepted by use (§1.2).
 *
 * The label states the three facts it is a consent TO — amount, interval, and
 * that it continues until cancelled — because a checkbox whose label says only
 * "I agree" is a checkbox nobody can say what they agreed to.
 */
export const RENEWAL_CONSENT = "Charge me this amount, at this interval, until I cancel.";

/**
 * The trial's own control, because a trial is its own negative option: nothing
 * is charged today and something is charged later unless the person acts, and a
 * consent that did not say so would be a consent to a different offer. Beside
 * the "Start the trial" button, exactly as the renewal consent sits beside the
 * ladder it authorises.
 */
export const TRIAL_CONSENT = "Start the trial; charge me when it ends unless I cancel.";

export const CANCEL_LABEL = "Cancel plan";
export const CANCEL_CONFIRM_LABEL = "Cancel my plan";
export const CANCEL_KEEP_LABEL = "Keep it";
export const CANCEL_QUESTION = "Cancel your plan?";
export const CANCEL_NOTE =
  "Nothing is deleted. Your door stays live, your rules and your threads " +
  "are untouched, and your plan runs to the end of the period you have paid for.";
/**
 * WHAT IS CHARGED, ON THE CANCEL CONFIRMATION TOO (ruling R-0909-1). Every
 * confirmation in this room states what changes, when, and what is charged —
 * and for a cancellation the answer to the third is the reassuring one, which
 * is exactly why it has to be said rather than left to be inferred from the
 * absence of a figure.
 */
export const CANCEL_CHARGE_NOTE = "Nothing more is charged.";
/**
 * The same fact, from inside a trial. A trial that is cancelled never becomes a
 * paid plan, so "your plan runs to the end of the period you have paid for" is
 * the wrong sentence: nothing was paid for and nothing will be.
 */
export function cancelTrialLine(trialEnd: string): string {
  return `Your trial ends on ${statedDate(trialEnd)} and nothing is charged.`;
}
/** The server's own words, kept the same here so the two never drift apart. */
export const NO_ROLLOVER = "Unused allowance does not roll over.";

/** What the annual toggle actually buys, on the two rungs that sell it. */
export const ANNUAL_COPY = "Annual is a real discount, on the two plans that offer it.";

/** The inline way out of a trial, beside the sentence that says it will charge. */
export const TRIAL_CANCEL_LABEL = "Cancel";

/**
 * THE WAY BACK FROM A SCHEDULED CHANGE (ruling R-0908-1). While a change is
 * pending — a boundary downgrade, or a trial-end change in either direction
 * (R-0909-1) — the rung the owner is currently on is not "On it" — it is a thing
 * they can choose again, and choosing it takes the pending change back. The
 * label says what the tap does rather than what the rung is, because "On it"
 * greyed out beside "Moving to Lite on the 4th" is exactly the dead end the
 * owner reported.
 */
export const KEEP_PLAN_LABEL = "Keep this plan";

/**
 * WHAT EVERY PAID RUNG IS BILLED AS, said on the rung (owner, 2026-08-26).
 *
 * It used to be one hardcoded "billed monthly" under a rung with no ratio, and
 * one sentence — "Month to month. The Pro plans are not sold by the year." —
 * under the two rungs that had no annual price. Neither told a buyer looking at
 * the YEARLY tab what they were about to be charged and how often, which is the
 * one fact the interval control changes and the one the disclosure beside the
 * button has to be read in full to recover.
 *
 * So every paid rung carries its own line, and it follows the tab: Lite and
 * Personal say which interval is selected, and the two Pro rungs — which are not
 * sold by the year at all (§4) — say the same thing whichever tab is showing,
 * with the promise `no_commitment` actually stands for attached to it.
 */
export function intervalLine(rung: LadderRung, interval: PlanInterval): string {
  if (rung.no_commitment) return "Billed monthly, cancel anytime.";
  return interval === "annual" ? "Billed annually" : "Billed monthly";
}

/**
 * THE TRIAL'S LIMIT, WITHOUT A NUMBER (FR-5.8 #3, economics-wire §2.3, ruling
 * R-0905-8). A trial's allowance is its tier's month scaled to the trial's
 * length — the counter runs exactly as a paid period's does, and the only
 * visible difference is the header on the bar. Version 1.0 pro-rated the
 * allowance and STATED the resulting figure; ruling C2 took the figure away for
 * good, so this sentence says what the allowance is sized to and never says
 * how much that is. It used to promise "the full limit, not a slice of one",
 * which R-0905-8 made untrue, and a checkout footnote may not be untrue.
 */
export function trialLimitLine(tierLabel: string): string {
  return `A ${tierLabel} allowance sized to the trial's length, not to a month.`;
}

/**
 * WHAT THE TRIAL COSTS WHEN IT ENDS, ON THE LINE THAT SAYS $0 (owner,
 * 2026-09-07).
 *
 * The sheet used to read "Pay $0 now." and then the disclosure paragraph, which
 * carries the price six lines down in prose — and the screen was read as not
 * stating a price at all. So the figure joins the sentence it qualifies. It is
 * the SERVER'S string, unit and all (`after_trial_monthly_display`): this side
 * neither divides an annual price by twelve nor formats a currency, and it does
 * not parse one out of the disclosure either.
 *
 * ONE SENTENCE, BOTH SURFACES — the wizard's money step and this room's trial
 * panel — so a buyer who sees the offer twice is not told two different things.
 */
export function trialPriceLine(afterTrialMonthlyDisplay: string): string {
  return `Pay $0 now. ${afterTrialMonthlyDisplay} after the trial ends.`;
}

/** How a rung describes itself: a RATIO of another rung, never a count (§4). */
export function ratioLine(rung: LadderRung): string | null {
  return rung.relative_to
    ? `${rung.relative_to.times}× ${TIER_NAMES[rung.relative_to.tier]}`
    : null;
}

/** The share, in prose. The same rule as the composer's meter: never a capacity. */
export function usageLine(view: PlanView): string {
  const { plan } = view;
  // UNMETERED IS NOT SPENT (ruling C10). Free has no allowance, so "this
  // period's work is used" would be false — there was never any to use. The
  // honest sentence names what the tier does, and the ladder below it is the
  // answer to the question it raises.
  if (!plan.metered) {
    return "This tier does no AI work. Your door is open and free; the doorman needs a plan.";
  }
  // THE SPENT PERIOD NAMES WHAT HAPPENS NEXT, NOT A PRICE (ToU 2.2 §5.3). It
  // used to say further work drew on credits and point at the wallet for the
  // figures; ruling R-0916-1 deleted both, and what is true now is that the
  // work waits. The roads out are the plan card's, below this line, and not
  // one of them is stated as a quantity.
  if (plan.overflow && plan.state === "past_due") return PAST_DUE_USED_LINE;
  if (plan.overflow) {
    // With pay as you go on, the wall that stopped the work is the maximum
    // the subscriber set, and the sentence says which wall it is.
    return view.payg?.on
      ? "This month's pay-as-you-go maximum is reached. Work pauses until you raise it or the allowance resets."
      : "This period's allowance is used. Work pauses until it resets.";
  }
  // PAST THE ALLOWANCE AND STILL WORKING (ruling R-0923-3 (8)): a share past
  // 100%, said as one, with no figure of money — the dollars are below.
  if (plan.used_share > 1) {
    return `${Math.round(plan.used_share * 100)}% of this month's allowance used — pay as you go is on.`;
  }
  const used = Math.round(plan.used_share * 100);
  if (used === 0) return "None of this month's allowance used yet.";
  if (used < 10) return "Barely any of this period used.";
  if (used < 40) return "About a third of this period used.";
  if (used < 60) return "About half of this period used.";
  if (used < 85) return "Most of this period used.";
  return "Nearly all of this period used.";
}

/**
 * "Resets on 13 October 2026" — or, on a trial, when the trial ENDS (owner,
 * 2026-09-23): a trial's window does not reset, it becomes the paid plan.
 */
export function resetsLine(plan: PlanView["plan"]): string {
  if (plan.state === "trialing")
    return `Trial ends on ${statedDate(plan.trial_end ?? plan.resets_on)}`;
  return `Resets on ${statedDate(plan.resets_on)}`;
}

/**
 * The notices one box draws: the owner's (ruling R-0923-1 (5)) and pay as you
 * go's (R-0923-3 (8)), the server's words both. Pay as you go's carry no id of
 * their own, so they are keyed by what they say.
 */
export function noticesOf(view: PlanView): { id: string; body: string; until: string }[] {
  const payg = (view.payg?.notices ?? []).map((body) => ({ id: `payg:${body}`, body, until: "" }));
  return [...payg, ...view.notices];
}

export function statedDate(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * "the 4th" — the ruling's own way of putting a date on a BUTTON (R-0909-1).
 *
 * The full date is in the sentence directly above it, where §9 #1 needs it
 * unambiguous; the button is where somebody's eye lands last, and "Move to Lite
 * on 1 October 2026" reads as a form field. The two are never apart: this
 * shorthand only ever appears under a line that already stated the month.
 */
export function dayOrdinal(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const day = at.getUTCDate();
  const tens = day % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";
  return `the ${day}${suffix}`;
}

/**
 * EVERY PLAN CHANGE IS CONFIRMED, AND THE CONFIRMATION SAYS THREE THINGS
 * (ruling R-0909-1).
 *
 * The owner, on staging: an upgrade, a downgrade and a cancel all "look like
 * nothing happens" — a toast, and the fact only visible after scrolling to the
 * status line. Choosing a rung while on a trial was worse, because it also
 * "guesses what they want".
 *
 * So a tap on a rung now opens a question, and the question states, in plain
 * sentences: **what changes**, **when it takes effect**, and **what is charged
 * and when**. The four functions below are those sentences and the button's
 * label. They are exported because they are the ruling: a test asserts the
 * words, not a screenshot.
 *
 * TWO THINGS ARE NEVER IN THEM. Not a capacity, in any form (ruling C2) — a
 * plan is described by its price here as everywhere else. And not a price this
 * side composed: every figure below is a string that arrived from the server,
 * interpolated whole, never added to, divided or reformatted.
 */
export function changeQuestion(preview: PlanChangePreview): string {
  if (preview.direction === "kept") return `Keep ${TIER_NAMES[preview.tier]}?`;
  return `Move to ${TIER_NAMES[preview.tier]}?`;
}

/** What changes — from rung to rung, or from one billing interval to the other. */
export function changeEffectLine(preview: PlanChangePreview): string {
  const to = TIER_NAMES[preview.tier];
  const from = TIER_NAMES[preview.from_tier];
  if (preview.direction === "kept") {
    return `Stay on ${from}. The change you asked for is off.`;
  }
  if (preview.tier === preview.from_tier) {
    const was = preview.from_interval === "annual" ? "yearly" : "monthly";
    const now = preview.interval === "annual" ? "yearly" : "monthly";
    return `You are on ${from}, billed ${was}. This moves it to ${now} billing.`;
  }
  return `You are on ${from}. This moves you to ${to}.`;
}

/** When it takes effect — a date, or "now". A trial's two answers differ here. */
export function changeTimingLine(preview: PlanChangePreview, option: PlanChangeOption): string {
  const to = TIER_NAMES[preview.tier];
  if (preview.direction === "kept") return "Nothing changes and nothing moves.";
  if (option.when === "now") {
    return `Your trial ends today and ${to} starts now.`;
  }
  if (option.when === "at_trial_end") {
    return `Your trial runs to ${statedDate(option.effective_at)}, and ${to} starts then.`;
  }
  return `It takes effect ${
    option.charge_now_display !== null ? "today" : `on ${statedDate(option.effective_at)}`
  }.`;
}

/**
 * What is charged and when. `charge_now_display` is a figure whenever anything
 * is billed today — including `$0`, which is a real answer to a proration that
 * netted to nothing — and null only where the change is scheduled and today's
 * invoice is genuinely empty. A preview that could not reach the processor
 * never gets this far: the server refuses it rather than letting this sentence
 * understate a charge.
 */
export function changeChargeLine(preview: PlanChangePreview, option: PlanChangeOption): string {
  const every = preview.interval === "annual" ? "a year" : "a month";
  if (preview.direction === "kept") {
    return option.next_charge_at
      ? `Nothing is charged today. Your next charge is ${option.next_charge_display} on ${statedDate(option.next_charge_at)}.`
      : `Nothing is charged today. Your plan carries on at ${option.next_charge_display} ${every}.`;
  }
  if (option.charge_now_display !== null) {
    return `${option.charge_now_display} is charged today, then ${option.next_charge_display} ${every} after that.`;
  }
  return option.next_charge_at
    ? `Nothing is charged today. Your next charge is ${option.next_charge_display} on ${statedDate(option.next_charge_at)}.`
    : `Nothing is charged today, then ${option.next_charge_display} ${every}.`;
}

/** The primary button's words: the action, the rung, and the timing. */
export function changeActionLabel(preview: PlanChangePreview, option: PlanChangeOption): string {
  const to = TIER_NAMES[preview.tier];
  if (preview.direction === "kept") return KEEP_PLAN_LABEL;
  if (option.when === "now") return `Start ${to} now`;
  if (option.when === "at_trial_end") {
    return `Start ${to} when the trial ends on ${dayOrdinal(option.effective_at)}`;
  }
  return option.charge_now_display !== null
    ? `Move to ${to} now`
    : `Move to ${to} on ${dayOrdinal(option.effective_at)}`;
}

/** The one way back out of the question, on every one of these dialogs. */
export const CHANGE_DISMISS_LABEL = "Not now";

type Props = {
  view: PlanView | null;
  loading: boolean;
  busy: boolean;
  /**
   * The tier an accepted change is moving to, while the read still shows the
   * old one (wave 124): the plate says "Updating…" rather than naming a tier
   * that is about to stop being current.
   */
  updatingTo?: PlanTier | null;
  /** The change has been pending past the short re-read: say so, offer "Check again". */
  stalled?: boolean;
  checking?: boolean;
  onCheckAgain?: () => void;
  /** The status line, so the area can take focus there after the confirm. */
  statusRef?: Ref<HTMLParagraphElement>;
  /** What the always-mounted live region says (the landing). */
  announce?: string;
  note: string | null;
  error: string | null;
  /** The confirmation is open — one screen, one primary action (§9 #4). */
  cancelling: boolean;
  /**
   * THE CHANGE THE OWNER IS BEING ASKED TO CONFIRM (ruling R-0909-1), or null
   * when nothing is being asked. It arrives from the server before anything has
   * been sent — `onChoose` fetches it, this renders it, and only the button
   * inside it commits.
   */
  change: PlanChangePreview | null;
  /**
   * Opened by "Get more usage" in a conversation that met the wall (wave 57):
   * the options come into view — the plan card with the rung above where a
   * spent paid plan has one, the ladder otherwise.
   */
  toOptions?: boolean;
  /**
   * Opened by a conversion gate's "Start your free trial" (ruling R-1006-2): the
   * trial's own consent comes into view and takes focus. The consent stays
   * here, where it lives; the gate only brings somebody to it.
   */
  toTrial?: boolean;
  /** The "Update card" control the past-due block carries (ruling R-1006-3). */
  updateCard?: ReactNode;
  onChoose: (offer: PlanOffer) => void;
  onConfirmChange: (option: PlanChangeOption) => void;
  onDismissChange: () => void;
  onAskCancel: () => void;
  onDismissCancel: () => void;
  onConfirmCancel: () => void;
};

/**
 * The plan room (docs/plans.md §14.3). A pure render: what you are on, how much
 * of the period is gone, the ladder you could move to, and — never more than
 * two taps from here — how to stop.
 *
 * **NO QUANTITY IS ON THIS SCREEN, INCLUDING ON THE LADDER** (ruling C2). Usage
 * is a share of a period and the capacity behind it stays server-side — and
 * ruling C2 extends that to CHECKOUT, which is what changed here: the ladder
 * used to state a task count per rung, justified as "a buyer must see what they
 * commit to". A published quantity is a promise we would be forced to break the
 * first time a model reprices. What a rung states instead is its PRICE, which is
 * not a capacity and which the auto-renewal rules require, and its RATIO to
 * another rung. The fair-use clause in the server's disclosure is what stands
 * where the number used to be, and `plans/capacity.test.ts` greps this bundle to
 * keep it that way.
 */
export function PlanScreen({
  view,
  loading,
  busy,
  updatingTo = null,
  stalled = false,
  checking = false,
  onCheckAgain,
  statusRef,
  announce = "",
  note,
  error,
  cancelling,
  change,
  toOptions = false,
  toTrial = false,
  updateCard = null,
  onChoose,
  onConfirmChange,
  onDismissChange,
  onAskCancel,
  onDismissCancel,
  onConfirmCancel,
}: Props) {
  const [accepted, setAccepted] = useState(false);
  /** The trial's own tick (ToU 2.1 §8.2) — a different offer, a different consent. */
  const [trialAccepted, setTrialAccepted] = useState(false);
  /**
   * The UK/EEA withdrawal-right acknowledgement (§8.6). Only ever rendered when
   * the server sent the sentence, and required of BOTH paths when it was: a
   * trial converts into the same subscription a paid start creates, so starting
   * one inside the fourteen days needs the same acknowledgement.
   */
  const [waived, setWaived] = useState(false);
  // `view` is null while the room is loading; nothing below it renders then,
  // and an absent view is not a jurisdiction that needs the control.
  const waiverSatisfied = !view?.withdrawal_waiver || waived;
  /**
   * ANNUAL IS THE TAB THAT IS SHOWING (owner, 2026-08-26). It is rendered first
   * in the control, it is the interval this product would rather sell, and it
   * is the cheaper of the two on both rungs that offer it — a default that
   * opened on the dearer one was a segmented control arguing against itself.
   * The two Pro rungs are unaffected: they are not sold by the year at all and
   * `offerFor` falls back to their one price (§4).
   */
  /**
   * …AND FOR A SUBSCRIBER, THEIR OWN INTERVAL (owner, 2026-09-23): somebody
   * paying monthly who opens the ladder is looking at what their own prices
   * would be, and a yearly tab under them reads as a price change nobody
   * asked about. Annual stays the opening tab for everybody not yet paying,
   * except when a gate sends them to the trial: that opens on Monthly, so the
   * price the gate quoted is the one in the room (ruling R-1006-2).
   */
  const [interval, setInterval] = useState<PlanInterval>(
    view?.plan.interval ?? (toTrial ? "monthly" : "annual"),
  );
  // The screen can mount before the plan has arrived; the tab is placed once,
  // on the first read that says what the subscriber pays, and never after —
  // a refetch must not take a tab somebody chose away from them.
  const intervalPlaced = useRef(view !== null);
  useEffect(() => {
    if (intervalPlaced.current || !view) return;
    intervalPlaced.current = true;
    if (view.plan.interval) setInterval(view.plan.interval);
  }, [view]);

  /**
   * THE CONFIRMATION HAS TO BE WHERE THE EYES ARE (owner, 2026-08-26). The way
   * out sits at the FOOT of a long ladder and the confirmation it opens sits
   * ABOVE that ladder — so pressing "Cancel plan" at the bottom of the panel
   * used to open a question three screens up, out of sight, and read as the
   * button having done nothing. Bringing it into view is half the fix; taking
   * focus is the other half, and it is the half a screen reader needs, because
   * a question that is announced nowhere has not been asked.
   */
  const confirmHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!cancelling) return;
    const heading = confirmHeading.current;
    if (!heading) return;
    heading.scrollIntoView?.({ block: "center" });
    heading.focus();
  }, [cancelling]);

  /**
   * THE CHANGE CONFIRMATION GETS THE SAME TREATMENT, FOR THE SAME REASON
   * (ruling R-0909-1). The ladder is long, the rung that was tapped can be
   * anywhere on it, and a question that opens off-screen is a question nobody
   * was asked — which is what "changes look like nothing happens" described.
   * Focus is the half a screen reader needs; the scroll is the half everybody
   * else does.
   */
  const changeHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!change) return;
    const heading = changeHeading.current;
    if (!heading) return;
    heading.scrollIntoView?.({ block: "center" });
    heading.focus();
  }, [change]);

  /**
   * "GET MORE USAGE" LANDS ON THE OPTIONS (wave 57). The conversation's one
   * control said what pressing it does — it opens the options — so the view
   * opens there rather than on the lede. Once, on the first render that has a
   * plan to place the anchor in, so a refetch never yanks the reader back.
   */
  const trialBlock = useRef<HTMLDivElement>(null);
  const trialConsent = useRef<HTMLInputElement>(null);
  const trialPlaced = useRef(false);
  useEffect(() => {
    if (!toTrial || trialPlaced.current || !trialBlock.current) return;
    trialPlaced.current = true;
    trialBlock.current.scrollIntoView?.({ block: "center" });
    trialConsent.current?.focus();
  });
  const options = useRef<HTMLDivElement>(null);
  const placed = useRef(false);
  useEffect(() => {
    if (!toOptions || placed.current || !options.current) return;
    placed.current = true;
    options.current.scrollIntoView?.({ block: "start" });
  });

  if (!view) {
    return (
      <div className="nob-enter py-4">
        <Group>
          <div className={PLATE}>
            <p className="min-h-[3.5rem] text-[13px] text-faint">
              {loading ? <span className="nob-breathe">Reading your plan.</span> : null}
            </p>
          </div>
        </Group>
      </div>
    );
  }

  const { plan, ladder, offers, trial } = view;
  const onPaid = plan.tier !== "free";
  const ending = plan.cancel_requested_at !== null;
  /**
   * WHETHER THE PLAN CARD STANDS IN "NOW": a paid plan (not a trial) whose
   * window is spent, with a rung above it to move to. Where the paid reset used
   * to be offered (ruling R-0923-3 (1) deleted it), until pay as you go joins
   * the card in wave 67.
   */
  /**
   * "GET MORE USAGE" (ruling R-0923-3 (3), docs/plans.md §5.4): at the wall —
   * the allowance without pay as you go, or the maximum with it — at the 80%
   * notice. The rung above where there is one, and pay as you go beside it; on
   * Pro 20× pay as you go alone. A trial and Free have the ladder instead
   * (§8.3).
   */
  const pastDue = plan.state === "past_due";
  const offerMore =
    !pastDue &&
    plan.metered &&
    !startPlanRoad(plan) &&
    (plan.overflow || plan.usage_notice === "approaching") &&
    (plan.next_tier !== null || view.payg !== null);

  /**
   * THE WAY BACK, WHERE THE CHANGE IS ANNOUNCED (ruling R-0908-1). The offer
   * for the rung the owner is on, which — while a change is scheduled — undoes
   * it. It goes in the "Now" plate beside the sentence naming the change, the
   * same shape the trial's own "unless you cancel" line uses, because the
   * ladder below is three screens down and its buttons follow the interval tab:
   * a monthly subscriber reading the Yearly tab would not find it there at all.
   */
  const keepOffer =
    plan.scheduled_tier !== null
      ? (offers.find((o) => o.tier === plan.tier && o.interval === plan.interval) ?? null)
      : null;

  /**
   * The offer a rung would be bought under at the SELECTED interval — falling
   * back to monthly for a rung that is not sold annually at all (`no_commitment`,
   * §4). The fallback is not a fudge: those rungs have exactly one price, the
   * line beneath them says why, and offering an annual button that would 400 at
   * the processor would be worse than either.
   */
  function offerFor(rung: LadderRung): PlanOffer | null {
    const wanted = rung.no_commitment ? "monthly" : interval;
    return offers.find((o) => o.tier === rung.tier && o.interval === wanted) ?? null;
  }

  const trialOption =
    trial?.options.find((o) => o.interval === interval) ?? trial?.options[0] ?? null;

  return (
    /**
     * THE SETTINGS ANATOMY, ON THE MONEY VIEWS TOO (owner item 6, 2026-08-27).
     *
     * Settings' pages stand on plates — a `bg-bg` card inset from the panel's
     * `sidebar` ground, with rows contained inside it — and this view did not.
     * It was free-flowing prose and floating cards on the same yellow, which
     * read as a different product from the panel one chevron away. Nothing
     * about WHAT is here changed, or in what order; every block is now a
     * `Group`, and the plate is what makes the 34rem column read as settings.
     */
    <div className="nob-enter py-4">
      {/* NO HEADING OF ITS OWN. This screen is only ever a view inside the
          billing panel, and the panel's header already names it; an `h1` here
          would be the same word twice, forty pixels apart. The lede stays,
          because what a plan actually pays for is not obvious from the word.
          It is a note strip above the first plate, the shape Settings gives a
          sentence that explains a page rather than governing anything on it. */}
      <p className="px-6 pb-3 text-[12px] leading-snug text-muted">{PLAN_LEDE}</p>

      {/* The owner's notices (ruling R-0923-1 (5)) and pay as you go's own
          (R-0923-3 (8)): at the top, above the usage line they explain, and
          only when the server sent some. Never inside a hearing. */}
      <PlanNotices notices={noticesOf(view)} className="mx-3 mb-4" />

      {/* What you are on, and how much of the period is gone. A share, in
          words — the arithmetic behind it never leaves the server (ruling C2). */}
      <Group title="Now">
        <div className={PLATE}>
          <p className="font-mono text-xl tracking-tight text-accent-ink">
            {updatingTo ? "Updating…" : TIER_NAMES[plan.tier]}
            {!updatingTo && plan.state === "trialing" && (
              <span className="text-faint"> · trial</span>
            )}
            {!updatingTo && plan.state === "past_due" && (
              <span className="text-faint"> · payment overdue</span>
            )}
          </p>
          {updatingTo && (
            <div data-updating={updatingTo}>
              <p
                ref={statusRef}
                tabIndex={-1}
                className="mt-1.5 text-[13px] leading-relaxed text-muted outline-none"
              >
                {stalled
                  ? `Your change to ${TIER_NAMES[updatingTo]} was accepted and is still being applied. ` +
                    "This page will show it once it lands — no need to choose again."
                  : `Moving you to ${TIER_NAMES[updatingTo]}. This takes a few seconds.`}
              </p>
              {stalled && (
                <SecondaryButton
                  type="button"
                  onClick={onCheckAgain}
                  disabled={checking}
                  className="mt-2 h-8 px-3"
                >
                  Check again
                </SecondaryButton>
              )}
            </div>
          )}
          <p className="mt-1.5 text-[13px] leading-relaxed text-muted">{usageLine(view)}</p>
          <p className="mt-1 text-[11px] text-faint">
            {plan.resets_on ? `${resetsLine(plan)}. ` : ""}
            {NO_ROLLOVER}
          </p>
          {/* THE NEGATIVE-OPTION SENTENCE, AND THE WAY OUT OF IT, TOGETHER
              (owner, 2026-08-26). "unless you cancel" names an action, and §9 #4
              asks for that action to be self-serve and no further away than
              subscribing was — so it is a link in the sentence itself rather
              than a word pointing at a control at the foot of the screen. It
              opens the SAME confirmation the bottom link does; there is one
              cancel path in this product and this is a second door onto it, not
              a second path.

              AND IT IS GONE ONCE CANCELLED. A trial that has been cancelled does
              not become a paid plan on any date, so the sentence saying it will
              would be false — `ending` below is the only true thing left to say,
              and the two are mutually exclusive rather than stacked. */}
          {plan.state === "trialing" && plan.trial_end && !ending && (
            <p className="mt-2 text-[13px] leading-snug text-text-soft">
              Your trial becomes a paid plan on {statedDate(plan.trial_end)} unless you cancel.{" "}
              <LinkButton type="button" onClick={onAskCancel} disabled={busy}>
                {TRIAL_CANCEL_LABEL}
              </LinkButton>
            </p>
          )}
          {ending && (
            <p className="mt-2 text-[13px] leading-snug text-text-soft">
              Ending on {statedDate(plan.period_end)}. Nothing is deleted; you drop to Free.
            </p>
          )}
          {plan.scheduled_tier && (
            <p className="mt-2 text-[13px] leading-snug text-text-soft">
              Moving to {TIER_NAMES[plan.scheduled_tier]} on {statedDate(plan.scheduled_at)}.{" "}
              {keepOffer && (
                <LinkButton type="button" onClick={() => onChoose(keepOffer)} disabled={busy}>
                  {KEEP_PLAN_LABEL}
                </LinkButton>
              )}
            </p>
          )}

          {/* A DISPUTE IS OPEN (ToU 2.2 §8.9). One quiet line, no action and no
              amount: nothing on this screen can be bought past it, so a control
              beside it would be a road that goes nowhere. */}
          {plan.paid_features_suspended && (
            <p className="mt-2 text-[13px] leading-snug text-text-soft" role="status">
              {PAID_SUSPENDED_LINE}
            </p>
          )}

          {/* THE WAY PAST A SPENT PAID PLAN: the rung above, priced, on the
              plan card, because somebody who came looking for their plan after
              meeting the wall came looking for exactly this. (The paid reset
              that stood here is deleted, ruling R-0923-3 (1).) */}
          {offerMore && (
            <div ref={options} className="scroll-mt-4" data-plan-options>
              <PlanCard
                nextTier={plan.next_tier}
                ladder={view.ladder}
                startPlan={startPlanRoad(plan)}
                payg={view.payg}
                priceSuffix={view.tax.suffix}
              />
            </div>
          )}
        </div>
      </Group>

      <div className="min-h-[1.25rem] px-6 pb-2" aria-live="polite">
        {/* The landing of a change is said here, in the region that is always
            mounted: "You're on Pro 5× now." (wave 124) */}
        <span className="sr-only" data-announce>
          {announce}
        </span>
        {note && <p className="nob-ack font-mono text-[11px] text-accent-ink/80">{note}</p>}
        {error && (
          <p className="text-[13px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* THE PLAN-CHANGE CONFIRMATION (ruling R-0909-1). Every change passes
          through here — up, down, an interval switch and the undo of a
          scheduled one. It states what changes, when it takes effect and what
          is charged, in that order, and the only thing on it that commits is
          the button whose label repeats the whole action.

          ON A TRIAL IT ASKS RATHER THAN ASSUMES: two buttons, both primary,
          neither pre-selected, each with its own date. That is the whole of
          "a trial is named". */}
      {change ? (
        <Group>
          <div className={`${PLATE} border-l-2 border-border-strong`} data-change-confirm>
            <h2
              ref={changeHeading}
              tabIndex={-1}
              className="text-[15px] font-semibold text-text focus-visible:outline-none"
            >
              {changeQuestion(change)}
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-muted">
              {changeEffectLine(change)}
            </p>
            {/* The figures below include VAT or GST where the account's country
                expects it (ruling R-0923-5); the server's own sentence says so.
                Elsewhere the charge line itself says "plus tax". */}
            {view?.tax.inclusive && view.tax.line && (
              <p className="mt-1 text-[12px] leading-relaxed text-faint" data-tax-line>
                {view.tax.line}
              </p>
            )}
            <div className="mt-4 space-y-4">
              {change.options.map((option) => (
                <div key={option.when ?? "only"}>
                  <p className="text-[13px] leading-snug text-text-soft">
                    {changeTimingLine(change, option)}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-text-soft">
                    {changeChargeLine(change, option)}
                  </p>
                  <Button
                    type="button"
                    onClick={() => onConfirmChange(option)}
                    disabled={busy}
                    className="mt-2 h-10 px-4"
                  >
                    {changeActionLabel(change, option)}
                  </Button>
                </div>
              ))}
            </div>
            {/* ONE WAY BACK, once, however many ways forward there are. */}
            <div className="mt-4">
              <LinkButton type="button" onClick={onDismissChange} disabled={busy}>
                {CHANGE_DISMISS_LABEL}
              </LinkButton>
            </div>
          </div>
        </Group>
      ) : null}

      {/* THE CONFIRMATION. One question, one primary action, and the retention
          line is dismissed by that same action (§9 #4) — it is a suggestion
          beside the button, never a gate in front of it. */}
      {cancelling ? (
        <Group>
          {/* A plate of its own, with a rule down its left edge: this is the
              one block on the view that is ASKING something rather than stating
              it, and the ladder below is long enough that a plate which looked
              like every other plate would be scrolled past. */}
          <div className={`${PLATE} border-l-2 border-border-strong`}>
            {/* `tabIndex={-1}` so the heading can HOLD focus without joining the
                tab order: the question is announced when it opens, and Tab from
                here still lands on the primary action rather than on a heading. */}
            <h2
              ref={confirmHeading}
              tabIndex={-1}
              className="text-[15px] font-semibold text-text focus-visible:outline-none"
            >
              {CANCEL_QUESTION}
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-muted">{CANCEL_NOTE}</p>
            {/* WHEN IT ENDS AND WHAT IS CHARGED, on the cancel confirmation as
                on every other one (ruling R-0909-1). A trial says its own
                sentence: it never becomes a paid plan, so "the period you have
                paid for" is not a thing it has. */}
            {plan.state === "trialing" && plan.trial_end ? (
              <p className="mt-2 text-[13px] leading-snug text-text-soft">
                {cancelTrialLine(plan.trial_end)}
              </p>
            ) : (
              plan.period_end && (
                <p className="mt-2 text-[13px] leading-snug text-text-soft">
                  Your plan would run until {statedDate(plan.period_end)}. {CANCEL_CHARGE_NOTE}
                </p>
              )
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button type="button" onClick={onConfirmCancel} disabled={busy} className="h-10 px-4">
                {CANCEL_CONFIRM_LABEL}
              </Button>
              <LinkButton type="button" onClick={onDismissCancel} disabled={busy}>
                {CANCEL_KEEP_LABEL}
              </LinkButton>
            </div>
          </div>
        </Group>
      ) : null}

      {/* The ladder. A purchase surface, so the PRICES are stated in full — and
          not one quantity, on any rung (ruling C2).

          ONE PLATE, TOP TO BOTTOM: the interval control, the trial, the consent
          and every rung. They are one decision — which interval, on which rung,
          having agreed to what — and Settings' anatomy for a block of things
          that belong to one decision is a plate with rows in it. */}
      {!offerMore && <div ref={options} className="scroll-mt-4" data-plan-options />}
      {pastDue ? (
        <Group title="Move">
          <div className={PLATE} data-past-due-change>
            <p className="text-[13px] leading-snug text-text-soft">{UPDATE_CARD_FIRST}</p>
            {updateCard ? <div className="mt-3 flex flex-wrap gap-2">{updateCard}</div> : null}
          </div>
        </Group>
      ) : (
        <Group title={onPaid ? "Move" : "Plans"}>
          {/* The interval, as one segmented control the width of the column —
            the reference build's shape. A pair of small buttons floating at the
            left read as two links rather than as one either/or. */}
          <div className={PLATE}>
            <div
              className="flex rounded-full bg-search p-0.5"
              role="group"
              aria-label="Billing interval"
            >
              {(["annual", "monthly"] as PlanInterval[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setInterval(option)}
                  aria-pressed={interval === option}
                  className={
                    "min-w-0 flex-1 rounded-full px-2 py-1.5 text-xs font-medium transition-colors " +
                    "duration-75 focus-visible:outline-none focus-visible:ring-2 " +
                    "focus-visible:ring-focus/70 " +
                    (interval === option
                      ? "bg-bg text-text shadow-sm"
                      : "text-muted hover:text-text")
                  }
                >
                  {option === "monthly" ? "Monthly" : "Yearly"}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] leading-snug text-muted">{ANNUAL_COPY}</p>
          </div>

          {/* THE DRAWDOWN DISCLOSURE (Terms 2.11 §5.3, §8.7): how the allowance is
            drawn down, said once on the screen where a plan or a trial is
            started, in every tax state. The server's sentence, verbatim; the
            purchase posts the hash of exactly what is rendered here. */}
          {!onPaid && view?.drawdown && (
            <p
              className="border-t border-border-soft px-3 py-3 text-[11px] leading-relaxed text-muted"
              data-testid="checkout-drawdown"
            >
              {view.drawdown}
            </p>
          )}

          {/* The trial, when there is one to offer. It names the ACTUAL tier
            (§8 #7), leads with what is charged today — nothing — and says the
            trial carries the FULL limit of that tier without saying what the
            limit is (§8 #3, ruling C2). */}
          {trial && trialOption && !onPaid && (
            <div
              ref={trialBlock}
              className="border-t border-border-soft bg-accent/[0.04] px-3 py-3"
              data-trial={trial.tier}
            >
              <p className="text-[13px] leading-snug text-text-soft">
                Try {TIER_NAMES[trial.tier]} free for {trial.days} days.
              </p>
              {/* The price, on the line that says $0 — the same sentence the
                wizard's money step leads with. */}
              <p className="mt-1 text-[13px] leading-snug text-text-soft">
                {trialPriceLine(trialOption.after_trial_monthly_display)}
              </p>
              <p className="mt-1 text-[13px] leading-snug text-text-soft">
                {trialLimitLine(TIER_NAMES[trial.tier])}
              </p>
              <p className="mt-2 text-[11px] leading-relaxed text-faint">
                {trialOption.disclosure}
              </p>
              {/* THE TRIAL'S OWN CONSENT (ToU 2.1 §8.2). A trial is its own
                negative option — $0 today, a charge later unless the person
                acts — so it gets its own control saying exactly that, beside
                the button it authorises, rather than sharing the ladder's. */}
              <label className="mt-3 flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-text-soft">
                <input
                  ref={trialConsent}
                  type="checkbox"
                  checked={trialAccepted}
                  onChange={(e) => setTrialAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong bg-surface accent-accent
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
                />
                <span>{TRIAL_CONSENT}</span>
              </label>
              <SecondaryButton
                type="button"
                disabled={busy || !trialAccepted || !waiverSatisfied}
                onClick={() =>
                  onChoose({
                    tier: trial.tier,
                    interval: trialOption.interval,
                    price_display: trialOption.price_display,
                    first_charge_on: trialOption.first_charge_on,
                    disclosure: trialOption.disclosure,
                    is_downgrade: false,
                  })
                }
                className="mt-3 h-9 px-4"
              >
                {START_TRIAL}
              </SecondaryButton>
            </div>
          )}

          {/* THE RENEWAL CONSENT, AND IT CONSENTS TO THE CHARGE ALONE (ToU 2.1
            §8.2). The Terms are no longer inside this tick: they are accepted
            by use (§1.2) and sit beside the control as a link, in their own
            sentence, outside the label — a document is not a thing a checkbox
            can honestly carry, and bundling the two made it impossible to say
            which of them somebody had agreed to. There is still no road to the
            processor that does not cross this checkbox. */}
          <label
            className="flex items-start gap-3 border-t border-border-soft px-3 pb-1 pt-3 text-[13px]
                     leading-relaxed text-text-soft"
          >
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong bg-surface accent-accent
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
            />
            <span>{RENEWAL_CONSENT}</span>
          </label>
          <p className="px-3 pb-3 pl-10 text-[11px] leading-relaxed text-faint">
            The{" "}
            <a
              href={`${API_URL}/terms`}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4 transition-colors hover:text-text-soft"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href={`${API_URL}/privacy`}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4 transition-colors hover:text-text-soft"
            >
              privacy policy
            </a>{" "}
            apply.
          </p>

          {/* THE WITHDRAWAL-RIGHT CONTROL, WHEN THE SERVER SAYS ONE APPLIES
            (ToU 2.1 §8.6, ruling R-0914-1 (3)). A second, distinct control for
            a consumer in the EEA or the UK: a plan starts doing AI work at
            once, so the buyer is asked to request that and to acknowledge what
            it costs them. The sentence is the SERVER'S — rendered verbatim and
            hashed as rendered — and the client never decides whether it
            applies. Everybody else sees nothing here. */}
          {view?.withdrawal_waiver && (
            <label
              className="flex items-start gap-3 border-t border-border-soft px-3 py-3 text-[13px]
                       leading-relaxed text-text-soft"
              data-withdrawal-waiver
            >
              <input
                type="checkbox"
                checked={waived}
                onChange={(e) => setWaived(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong bg-surface accent-accent
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
              />
              <span>{view.withdrawal_waiver}</span>
            </label>
          )}

          {/* EVERY RUNG, IN LADDER ORDER, FREE FIRST. Free is on the list on
            purpose: it is where a cancellation lands and where everybody
            starts, and a ladder whose bottom rung is invisible reads as though
            the product costs money to open a door — which it does not. */}
          {/* ROWS IN THE PLATE, NOT CARDS ON THE PANEL (owner item 6, 2026-08-27).
            They were free-standing cards with a gap between them, argued for on
            the grounds that a rung is a thing somebody picks. That argument was
            made when the ladder floated on the panel's own ground; inside the
            plate the CARD is the plate, and the rungs are its rows, which is
            what every other list in this shell's panels looks like. The current
            rung keeps its tint and its badge, so the one that is picked is still
            the one that reads as picked. */}
          <ul className="border-t border-border-soft">
            {ladder.map((rung) => {
              const offer = offerFor(rung);
              // While a change is being applied NO rung is current: the plan is
              // between two tiers, and tinting the old one read as the change
              // having failed (wave 124).
              const moving = updatingTo === rung.tier;
              const current =
                updatingTo === null &&
                plan.tier === rung.tier &&
                (rung.tier === "free" || offer === null || plan.interval === offer.interval);
              /**
               * The current rung, with a change scheduled off it, is the UNDO
               * (ruling R-0908-1). It is the only case where the rung somebody is
               * already on is worth tapping, and it is the primary action on the
               * ladder while it lasts — the change they want to take back is the
               * thing the screen is currently announcing above.
               */
              const keeping = current && plan.scheduled_tier !== null && offer !== null;
              const recommended = plan.next_tier === rung.tier;
              const Act = keeping || (recommended && !current) ? Button : SecondaryButton;
              // An annual price is shown as BOTH figures, and the per-month half
              // is the server's own division: the two have to be arithmetically
              // consistent or the card is a misrepresentation (§2.1).
              const annual = offer !== null && offer.interval === "annual";
              return (
                <li
                  key={rung.tier}
                  data-tier={rung.tier}
                  className={
                    "border-b border-border-soft px-3 py-3 last:border-b-0 " +
                    (current ? "bg-accent/[0.06]" : "")
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-[15px] font-semibold text-text">{rung.label}</span>
                        {current && (
                          <span
                            className="rounded border border-border-strong px-1.5 py-0.5 text-[10px]
                                     uppercase tracking-[0.08em] text-faint"
                          >
                            Current
                          </span>
                        )}
                        {recommended && !current && (
                          <span
                            className="rounded border border-accent/30 px-1.5 py-0.5 text-[10px]
                                     uppercase tracking-[0.08em] text-accent-ink-strong/80"
                          >
                            Next up
                          </span>
                        )}
                      </span>
                      {/* A RATIO, never a count — "3× Lite" says how the rungs
                        stand to each other and discloses no capacity (§4).
                        Where there is no ratio there is nothing to say here:
                        what a rung is BILLED as moved to its own line below,
                        where it can follow the interval control instead of
                        standing as one hardcoded word that the Yearly tab made
                        false. Free is the exception, and what it says is what
                        it does. */}
                      {/* Free says "No AI work" once, in the slot beside the
                        button below — twice was the same sentence nagging
                        (owner, 2026-08-26). */}
                      {ratioLine(rung) && (
                        <span className="mt-0.5 block text-xs text-faint">{ratioLine(rung)}</span>
                      )}
                    </span>

                    {offer && (
                      <span className="shrink-0 text-right">
                        <span className="block font-mono text-[15px] font-semibold tabular-nums text-text">
                          {annual ? rung.annual_per_month_display : rung.monthly_display}
                        </span>
                        <span className="mt-0.5 block font-mono text-[11px] tabular-nums text-faint">
                          {annual ? `a month, ${offer.price_display} a year` : "a month"}
                        </span>
                        {/* WHAT THE PRICE INCLUDES (ruling R-0923-5): "incl. VAT"
                          where the account's country expects prices with tax
                          in them — the figures above already include it — and
                          nothing where tax is added at checkout. */}
                        {view?.tax.suffix && (
                          <span
                            className="block font-mono text-[11px] tabular-nums text-faint"
                            data-tax-suffix
                          >
                            {view.tax.suffix}
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  <div className="mt-2.5 flex items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 text-xs text-faint">
                      {rung.metered ? "Monthly allowance, resets each month" : "No AI work"}
                    </span>
                    {/* THE RESULT IS SAID WHERE THE ACTION WAS (ruling
                      R-0909-1). The rung the owner picked carries the fact it
                      produced — "Starts on the 4th" — rather than leaving a
                      toast at the top of the room as the only sign that the tap
                      did anything. The status line above says it too; this says
                      it under the finger that pressed. */}
                    {moving ? (
                      <span className="shrink-0 text-xs text-accent-ink" data-moving={rung.tier}>
                        Moving…
                      </span>
                    ) : plan.scheduled_tier === rung.tier ? (
                      <span className="shrink-0 text-xs text-accent-ink" data-scheduled={rung.tier}>
                        Starts on {dayOrdinal(plan.scheduled_at)}
                      </span>
                    ) : offer ? (
                      <Act
                        type="button"
                        onClick={() => onChoose(offer)}
                        /**
                         * THE UNDO IS NOT GATED ON THE CONSENT TICK. Every other
                         * button here commits to a recurring amount and waits for
                         * the checkbox; this one WITHDRAWS a request and commits
                         * to nothing new, so making somebody re-agree to terms in
                         * order to take a change back would be the same friction
                         * §9 #4 forbids in front of cancelling (ruling R-0908-1).
                         */
                        disabled={
                          busy || (keeping ? false : current || !accepted || !waiverSatisfied)
                        }
                        className="h-9 shrink-0 px-4"
                      >
                        {keeping
                          ? KEEP_PLAN_LABEL
                          : current
                            ? "On it"
                            : offer.is_downgrade
                              ? "Move down"
                              : "Choose"}
                      </Act>
                    ) : (
                      <span className="shrink-0 text-xs text-fainter">
                        {current ? "On it" : "Free"}
                      </span>
                    )}
                  </div>

                  {/* WHAT THIS RUNG IS BILLED AS, on every paid rung and never on
                    Free, which is billed as nothing. On Lite and Personal it
                    follows the tab; on the two Pro rungs it does not move,
                    because they are not sold by the year at all (§4). */}
                  {rung.tier !== "free" && (
                    <p className="mt-2 text-[11px] text-fainter">{intervalLine(rung, interval)}</p>
                  )}

                  {/* The server's own sentence, whole, beside the control that
                    agrees to it. The client never composes this text and hashes
                    exactly what is rendered here. */}
                  {/* muted, not fainter: this is the ARL disclosure being consented to,
                    and the one rung that sits under AA is not a place for it. */}
                  {offer && !current && (
                    <p className="mt-2 text-[11px] leading-relaxed text-muted">
                      {offer.disclosure}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Group>
      )}

      {/* Free performs no AI work at all (ruling C10) — it is not a small
          allowance, it is none. What IS free, and always will be, is the door
          itself: being reachable, receiving knocks, reading them, answering
          by hand. A note strip under the plate, not a line inside it: it is
          true of the whole ladder rather than of any rung on it. */}
      <p className="px-6 pb-2 text-[11px] leading-snug text-muted">
        Opening a door and answering by hand are free, always. {NO_ROLLOVER}
      </p>

      {/* The way out, and it is one tap from here — no more steps than the two
          it took to start (§9 #4). Not a portal, not an email, not a phone call.
          Its own plate at the foot of the view, the way the door page puts its
          takedown on one: a plate is how this shell says "this block is on its
          own", and the way out of a plan is exactly that. */}
      {onPaid && !ending && !cancelling && (
        <Group>
          <div className={PLATE}>
            <LinkButton type="button" onClick={onAskCancel} disabled={busy}>
              {CANCEL_LABEL}
            </LinkButton>
          </div>
        </Group>
      )}
    </div>
  );
}
